import { useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import * as sessionUserSelector from 'selectors/sessionUser'
import {
  useFetchReleaseNotesQuery,
  useCreateReleaseNoteSubscriptionMutation,
  useRemoveReleaseNoteSubscriptionMutation,
} from 'api/releaseNotes'

import MainWrap from 'components/Shared/Skeleton/MainWrap/Index'
import Header from 'components/Shared/Header/Index'
import Content from 'components/Shared/Skeleton/Content/Index'
import ActionButtons from 'components/Shared/Skeleton/ActionButtons/Index'
import IconLink from 'components/Shared/IconLink/Index'
import ReleaseNote from './ReleaseNote'
import ReleasesListSidebar from './ReleasesListSidebar/Index'
import { RouteReleaseNotes, RouteReleaseNotesAdd, RouteReleaseNotesEdit } from 'routes'
import ReleaseSubscriptionLink from './ReleaseSubscriptionLink'

const ReleaseNotesPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const params = useParams()
  const selectedReleaseNoteId = params.releaseNoteId || null

  const { data, isFetching } = useFetchReleaseNotesQuery()

  const userIsAdmin = useSelector(sessionUserSelector.isAdmin)
  const userHasValidEmail = useSelector(sessionUserSelector.isValidEmail)
  const canAddEdit = userIsAdmin

  const releaseNotes = data?.releaseNotes || []
  const selectedReleaseNote = selectedReleaseNoteId ? releaseNotes.find(v => v.id === selectedReleaseNoteId) : null

  const [createSubscription, { isLoading: isCreatingSubscription }] = useCreateReleaseNoteSubscriptionMutation()
  const [removeSubscription, { isLoading: isRemovingSubscription }] = useRemoveReleaseNoteSubscriptionMutation()

  // select first release note is it was not specified in the URL
  useEffect(() => {
    if (isFetching) {
      return
    }

    if (!selectedReleaseNoteId && releaseNotes.length > 0) {
      const newReleaseNoteId = releaseNotes[releaseNotes.length - 1].id
      navigate(RouteReleaseNotes({ releaseNoteId: newReleaseNoteId }))
    }
  }, [isFetching]) // eslint-disable-line react-hooks/exhaustive-deps

  const onReleaseNoteSelected = (r) => {
    navigate(RouteReleaseNotes({ releaseNoteId: r.id }))
  }

  const subscribedToReleases = Boolean(data?.metaData?.subscribedToReleases)

  const onSubscribeToggle = () => {
    const toggleSubscription = subscribedToReleases ? removeSubscription : createSubscription

    toggleSubscription()
  }

  const getBreadcrumbs = () => {
    const breadcrumbsList = [{
      text: t('common.breadcrumbReleaseNotes'),
    }]
    return breadcrumbsList
  }

  const isUpdatingSubscription = isCreatingSubscription || isRemovingSubscription

  return (
    <MainWrap pageTitle={t('releaseNotesPage.header')}>
      <ReleasesListSidebar
        releaseNotes={releaseNotes}
        selectedReleaseNoteId={selectedReleaseNoteId}
        onReleaseNoteClick={onReleaseNoteSelected}
      />

      <Content loading={isFetching}>
        <Header
          title={t('releaseNotesPage.header')}
          breadcrumbsList={getBreadcrumbs()}
        />

        <ActionButtons>
          {canAddEdit && (
            <Link to={RouteReleaseNotesAdd()}>
              <IconLink
                iconName='icon-add-big-24'
                text={t('releaseNotesPage.addRelease')}
              />
            </Link>
          )}
          {canAddEdit && selectedReleaseNoteId && (
            <Link to={RouteReleaseNotesEdit({ releaseNoteId: selectedReleaseNoteId })}>
              <IconLink
                iconName='icon-edit'
                text={t('releaseNotesPage.editRelease')}
              />
            </Link>
          )}
          <ReleaseSubscriptionLink
            updatingSubscription={isUpdatingSubscription}
            subscribed={subscribedToReleases}
            onClick={onSubscribeToggle}
            isEmailValid={userHasValidEmail}
          />
        </ActionButtons>

        <ReleaseNote releaseNote={selectedReleaseNote} />
      </Content>
    </MainWrap>
  )
}

export default ReleaseNotesPage
